import { useState } from "react";
import { subscriptionsService } from "../../features/subscriptions/service";
import type { FeatureCatalogEntry } from "../../features/subscriptions/types";

interface FeatureCatalogEditorProps {
  entries: FeatureCatalogEntry[];
  onSaved: () => void;
}

/** Simple admin list of feature info entries (title + description) shown
 * via the (i) icon on the Pricing and Subscription pages. Admins can add,
 * edit, and delete entries; the public API returns this list as-is for any
 * client to apply tooltips wherever needed, matched by `action`. */
export default function FeatureCatalogEditor({ entries, onSaved }: FeatureCatalogEditorProps) {
  const [editingAction, setEditingAction] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ label: string; description: string } | null>(null);
  const [adding, setAdding] = useState(false);
  const [newEntry, setNewEntry] = useState({ label: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startEdit = (entry: FeatureCatalogEntry) => {
    setEditingAction(entry.action);
    setDraft({ label: entry.label, description: entry.description });
    setError(null);
  };

  const cancelEdit = () => {
    setEditingAction(null);
    setDraft(null);
    setError(null);
  };

  const save = async () => {
    if (!editingAction || !draft) return;
    if (!draft.label.trim() || !draft.description.trim()) {
      setError("Title and description cannot be empty");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await subscriptionsService.adminSetFeatureCatalog(editingAction, {
        label: draft.label.trim(),
        description: draft.description.trim(),
      });
      cancelEdit();
      onSaved();
    } catch {
      setError("Failed to save — please try again");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (action: string) => {
    if (!confirm("Delete this feature info entry?")) return;
    setSaving(true);
    setError(null);
    try {
      await subscriptionsService.adminDeleteFeatureCatalog(action);
      onSaved();
    } catch {
      setError("Failed to delete — please try again");
    } finally {
      setSaving(false);
    }
  };

  const addEntry = async () => {
    if (!newEntry.label.trim() || !newEntry.description.trim()) {
      setError("Title and description cannot be empty");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await subscriptionsService.adminCreateFeatureCatalog({
        label: newEntry.label.trim(),
        description: newEntry.description.trim(),
      });
      setNewEntry({ label: "", description: "" });
      setAdding(false);
      onSaved();
    } catch {
      setError("Failed to add — please try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold text-white">Feature Info List</h2>
          <p className="text-xs text-gray-500 mt-1">
            Title + description shown via the (i) icon on the Pricing and Subscription / Upgrade pages.
          </p>
        </div>
        <button
          onClick={() => {
            setAdding((a) => !a);
            setError(null);
          }}
          className="shrink-0 text-xs px-3 py-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-500"
        >
          {adding ? "Cancel" : "+ Add"}
        </button>
      </div>

      {adding && (
        <div className="px-6 py-4 border-b border-gray-800 space-y-2">
          <input
            value={newEntry.label}
            onChange={(e) => setNewEntry({ ...newEntry, label: e.target.value })}
            placeholder="Title"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
          <textarea
            value={newEntry.description}
            onChange={(e) => setNewEntry({ ...newEntry, description: e.target.value })}
            placeholder="Description"
            rows={2}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
          {error && <p className="text-xs text-red-400">{error}</p>}
          <button
            onClick={addEntry}
            disabled={saving}
            className="text-xs px-3 py-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-50"
          >
            {saving ? "Adding…" : "Add"}
          </button>
        </div>
      )}

      <div className="divide-y divide-gray-800/50">
        {entries.map((entry) => (
          <div key={entry.action} className="px-6 py-4">
            {editingAction === entry.action && draft ? (
              <div className="space-y-2">
                <input
                  value={draft.label}
                  onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
                <textarea
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  rows={2}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
                {error && <p className="text-xs text-red-400">{error}</p>}
                <div className="flex items-center gap-2">
                  <button
                    onClick={save}
                    disabled={saving}
                    className="text-xs px-3 py-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-50"
                  >
                    {saving ? "Saving…" : "Save"}
                  </button>
                  <button onClick={cancelEdit} className="text-xs px-3 py-1.5 rounded-md text-gray-400 hover:text-white">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-white">{entry.label}</p>
                  <p className="text-xs text-gray-400 mt-1">{entry.description}</p>
                </div>
                <div className="shrink-0 flex items-center gap-1">
                  <button
                    onClick={() => startEdit(entry)}
                    className="text-xs px-2 py-1 rounded-md text-indigo-400 hover:bg-indigo-500/10"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => remove(entry.action)}
                    className="text-xs px-2 py-1 rounded-md text-red-400 hover:bg-red-500/10"
                  >
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
